/**
 * Two kinds of temporary mark over the map: where the geometry is wrong, and where the
 * coordinate search landed.
 *
 * Both are `divIcon` markers rather than paths, so the shapes stay in CSS
 * (components/CoordSearch.css) where they were already described. There are only ever a
 * handful and they are transient by definition.
 *
 * The search pin is emphatically NOT a field: it has no geometry, is never saved, and goes
 * away with the next search or the Clear button.
 *
 * Every mark is deaf three times over — `interactive: false`, `keyboard: false` and
 * `pointer-events: none` in the CSS. The main product learned this the hard way: a mark sits
 * exactly where you have to click to fix the thing it is marking
 * (`frontend/…/DrawFieldMap.tsx:130-141`).
 */

import * as L from "leaflet";

import { toLatLng } from "./latlng";
import type { SearchTarget } from "../lib/places";

/**
 * Where a search landed, plus what the map needs to arrive properly.
 *
 * `box`/`zoom`/`label` arrive from a geocoded place (lib/places.ts) and are absent for a
 * pasted pair — a coordinate has no extent and nothing to read back but itself.
 */
export interface SearchPoint extends SearchTarget {
  /** Bumped per search so re-entering the same coordinates still flies there. */
  token: number;
}

const mark = (latlng: L.LatLngTuple, className: string, size: number, label: string): L.Marker => {
  const marker = L.marker(latlng, {
    icon: L.divIcon({
      className: `k-mark ${className}`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    }),
    interactive: false,
    keyboard: false,
  });
  marker.on("add", () => marker.getElement()?.setAttribute("aria-label", label));
  return marker;
};

/** A crossing or an out-of-range vertex. Non-interactive: it marks, it does not click. */
export const issueMarker = (point: [number, number]): L.Marker =>
  mark(toLatLng(point), "k-mark--issue", 14, "!");

export const searchMarker = (point: SearchPoint): L.Marker =>
  mark([point.lat, point.lon], "k-mark--search", 22, "+");
